import type { EntityDef } from "./entitySchema";

// JSON-shape hints: when iteration detection sees an array under one of these
// keys, it tags the array as that entity's iteration. About JSON conventions,
// not field vocabulary — stays a constant.
const ENTITY_PLURAL_HINTS: Record<string, string> = {
  claims: "Claim",
  procedures: "Procedure",
  payments: "Payment",
  patients: "Patient",
};

export type MissingRequiredField = {
  entity: string;
  field: string;
  label: string;
};

export function flattenJsonPaths(value: unknown, prefix = ""): string[] {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    if (value.length === 0) return prefix ? [`${prefix}[]`] : [];
    return flattenJsonPaths(value[0], `${prefix}[]`);
  }
  if (typeof value === "object") {
    const result: string[] = [];
    const obj = value as Record<string, unknown>;
    Object.keys(obj).forEach((key) => {
      const next = prefix ? `${prefix}.${key}` : key;
      result.push(...flattenJsonPaths(obj[key], next));
    });
    return result;
  }
  return prefix ? [prefix] : [];
}

export function detectIterationRules(
  value: unknown,
  prefix = "",
  parentEntity: string | null = null,
): StudioIterationRule[] {
  const rules: StudioIterationRule[] = [];

  if (Array.isArray(value)) {
    const lastSegment = prefix.split(".").pop()?.replace(/\[\]$/, "") ?? "";
    const entity = ENTITY_PLURAL_HINTS[lastSegment];
    if (entity) {
      rules.push({ jsonPath: prefix, entity, parentEntity });
      if (value.length > 0) {
        rules.push(...detectIterationRules(value[0], `${prefix}[]`, entity));
      }
    }
    return rules;
  }

  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    Object.keys(obj).forEach((key) => {
      const next = prefix ? `${prefix}.${key}` : key;
      rules.push(...detectIterationRules(obj[key], next, parentEntity));
    });
  }

  return rules;
}

export function bindingKey(entity: string, field: string): string {
  return `${entity}::${field}`;
}

export function getJsonValueAtPath(value: unknown, path: string): unknown {
  if (!path) return value;
  if (value === null || value === undefined) return undefined;
  const dotIdx = path.indexOf(".");
  const head = dotIdx === -1 ? path : path.slice(0, dotIdx);
  const rest = dotIdx === -1 ? "" : path.slice(dotIdx + 1);

  const arrayMatch = head.match(/^(.+)\[\]$/);
  const obj = value as Record<string, unknown>;
  if (arrayMatch) {
    const arr = obj[arrayMatch[1]];
    if (!Array.isArray(arr) || arr.length === 0) return undefined;
    return getJsonValueAtPath(arr[0], rest);
  }
  return getJsonValueAtPath(obj[head], rest);
}

export function formatPreviewValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") {
    return value.length > 50 ? `${value.slice(0, 50)}…` : value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  const json = JSON.stringify(value);
  return json.length > 50 ? `${json.slice(0, 50)}…` : json;
}

export function rankPathForEntity(
  path: string,
  entity: string,
  iterationRules: StudioIterationRule[],
): number {
  const rule = iterationRules.find((r) => r.entity === entity);
  const lastSegment =
    path.split(".").pop()?.replace(/\[\]$/, "").toLowerCase() ?? "";
  const entityLower = entity.toLowerCase();
  const pathLower = path.toLowerCase();

  if (rule) {
    const prefix = `${rule.jsonPath}[].`;
    if (path.startsWith(prefix)) {
      const remainder = path.slice(prefix.length);
      if (!remainder.includes("[]")) return 100;
      return 30;
    }
    return 5;
  }

  if (entity === "Patient") {
    if (pathLower.includes("[].patient.")) return 100;
    if (pathLower.includes("patient.")) return 80;
  }

  if (!path.includes("[]")) {
    if (pathLower.startsWith(`${entityLower}.`)) return 100;
    if (lastSegment.includes(entityLower)) return 70;
    return 50;
  }

  // Iteration rule missing but the array-nested path still names the entity.
  // E.g. `claim_lines[].claim_id` for Claim when no `claims[]` rule existed.
  if (lastSegment.includes(entityLower)) return 70;
  return 10;
}

// Auto-suggest: token-set matching with light normalization. Drops noise
// prefixes (`wieldy_`, `eop_`) and the entity name itself before splitting on
// `_`, then unifies common synonyms ("paid"/"pays", "no"/"number", etc.) so
// LLM-produced keys like `payment_amount` or `claim_paid` still align with the
// canonical wieldy field names.

const NOISE_PREFIXES = new Set(["wieldy", "eop"]);
const STOP_TOKENS = new Set(["of", "the", "a", "an"]);
const SYNONYMS: Record<string, string> = {
  pays: "paid",
  paid: "paid",
  no: "number",
  num: "number",
  amt: "amount",
  dob: "birth",
  birthdate: "birth",
  cdt: "code",
};

function tokenize(name: string, entity: string): Set<string> {
  const entityLower = entity.toLowerCase();
  const tokens = name
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(
      (t) =>
        t && t !== entityLower && !NOISE_PREFIXES.has(t) && !STOP_TOKENS.has(t),
    )
    .map((t) => SYNONYMS[t] ?? t);
  return new Set(tokens);
}

function tokenOverlap(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let shared = 0;
  a.forEach((t) => {
    if (b.has(t)) shared += 1;
  });
  return shared / Math.max(a.size, b.size);
}

export function suggestPathForField(
  entity: string,
  field: string,
  paths: string[],
  iterationRules: StudioIterationRule[],
): string | null {
  const fieldTokens = tokenize(field, entity);
  if (fieldTokens.size === 0) return null;

  let bestPath: string | null = null;
  let bestScore = 0;
  paths.forEach((p) => {
    const entityRank = rankPathForEntity(p, entity, iterationRules);
    if (entityRank < 70) return;

    const segments = p.split(/[.[\]]+/).filter(Boolean);
    const last = segments[segments.length - 1] ?? "";
    const overlap = tokenOverlap(fieldTokens, tokenize(last, entity));
    if (overlap < 0.5) return;

    const score = entityRank + Math.round(overlap * 200);
    if (score > bestScore) {
      bestScore = score;
      bestPath = p;
    }
  });

  return bestPath;
}

export type EntityFieldValue = {
  field: string;
  label: string;
  value: unknown;
  isDefault?: boolean;
};
export type ClaimPreview = {
  fields: EntityFieldValue[];
  patient: EntityFieldValue[] | null;
  procedures: EntityFieldValue[][];
};
export type PreviewTree = {
  payment: EntityFieldValue[];
  claims: ClaimPreview[];
};

function substituteIndices(path: string, indices: number[]): string {
  let i = 0;
  return path.replace(/\[\]/g, () => {
    const idx = indices[i] ?? 0;
    i += 1;
    return `[${idx}]`;
  });
}

function getIndexedJsonValue(json: unknown, path: string): unknown {
  if (!path) return json;
  if (json === null || json === undefined) return undefined;
  const dotIdx = path.indexOf(".");
  const head = dotIdx === -1 ? path : path.slice(0, dotIdx);
  const rest = dotIdx === -1 ? "" : path.slice(dotIdx + 1);
  const arrayMatch = head.match(/^(.+)\[(\d+)\]$/);
  const obj = json as Record<string, unknown>;
  if (arrayMatch) {
    const arr = obj[arrayMatch[1]];
    if (!Array.isArray(arr)) return undefined;
    return getIndexedJsonValue(arr[Number(arrayMatch[2])], rest);
  }
  return getIndexedJsonValue(obj[head], rest);
}

function resolveEntityFields(
  json: unknown,
  bindings: Record<string, string>,
  entity: string,
  indices: number[],
  entitySchema: EntityDef[],
): EntityFieldValue[] {
  const def = entitySchema.find((e) => e.name === entity);
  if (!def) return [];
  return def.fields
    .map((field): EntityFieldValue | null => {
      const path = bindings[bindingKey(entity, field.name)];
      if (path) {
        const resolved = getIndexedJsonValue(
          json,
          substituteIndices(path, indices),
        );
        if (resolved !== null && resolved !== undefined && resolved !== "") {
          return { field: field.name, label: field.label, value: resolved };
        }
      }
      if (field.defaultValue !== undefined) {
        return {
          field: field.name,
          label: field.label,
          value: field.defaultValue,
          isDefault: true,
        };
      }
      if (path) {
        return { field: field.name, label: field.label, value: null };
      }
      return null;
    })
    .filter((v): v is EntityFieldValue => v !== null);
}

function entityHasBindings(
  bindings: Record<string, string>,
  entity: string,
  entitySchema: EntityDef[],
): boolean {
  const def = entitySchema.find((e) => e.name === entity);
  if (!def) return false;
  return def.fields.some((f) => bindings[bindingKey(entity, f.name)]);
}

export function buildPreviewTree(
  json: unknown,
  bindings: Record<string, string>,
  iterationRules: StudioIterationRule[],
  entitySchema: EntityDef[],
): PreviewTree {
  const payment = resolveEntityFields(
    json,
    bindings,
    "Payment",
    [],
    entitySchema,
  );

  const claimRule = iterationRules.find((r) => r.entity === "Claim");
  const procRule = iterationRules.find(
    (r) => r.entity === "Procedure" && r.parentEntity === "Claim",
  );

  let claims: ClaimPreview[] = [];

  if (claimRule) {
    const claimArr = getIndexedJsonValue(json, claimRule.jsonPath);
    if (Array.isArray(claimArr)) {
      claims = claimArr.map((_item, claimIdx) => {
        const claimFields = resolveEntityFields(
          json,
          bindings,
          "Claim",
          [claimIdx],
          entitySchema,
        );
        const patientFields = entityHasBindings(
          bindings,
          "Patient",
          entitySchema,
        )
          ? resolveEntityFields(
              json,
              bindings,
              "Patient",
              [claimIdx],
              entitySchema,
            )
          : null;

        let procedures: EntityFieldValue[][] = [];
        if (procRule) {
          const procArrPath = substituteIndices(procRule.jsonPath, [claimIdx]);
          const procArr = getIndexedJsonValue(json, procArrPath);
          if (Array.isArray(procArr)) {
            procedures = procArr.map((_p, procIdx) =>
              resolveEntityFields(
                json,
                bindings,
                "Procedure",
                [claimIdx, procIdx],
                entitySchema,
              ),
            );
          }
        }

        return {
          fields: claimFields,
          patient: patientFields,
          procedures,
        };
      });
    }
  } else if (entityHasBindings(bindings, "Claim", entitySchema)) {
    const claimFields = resolveEntityFields(
      json,
      bindings,
      "Claim",
      [],
      entitySchema,
    );
    const patientFields = entityHasBindings(bindings, "Patient", entitySchema)
      ? resolveEntityFields(json, bindings, "Patient", [], entitySchema)
      : null;
    claims = [{ fields: claimFields, patient: patientFields, procedures: [] }];
  }

  return { payment, claims };
}

export function findMissingRequiredFields(
  bindings: Record<string, string>,
  entitySchema: EntityDef[],
): MissingRequiredField[] {
  const missing: MissingRequiredField[] = [];
  entitySchema.forEach((entity) => {
    const isActive = entity.fields.some(
      (f) => bindings[bindingKey(entity.name, f.name)],
    );
    if (!isActive) return;
    entity.fields.forEach((field) => {
      if (field.required && !bindings[bindingKey(entity.name, field.name)]) {
        missing.push({
          entity: entity.name,
          field: field.name,
          label: `${entity.label}.${field.label}`,
        });
      }
    });
  });
  return missing;
}
